import * as z from 'zod';
import {readOrganizationRefSchema} from '../organizations';
import {readBrandRefSchema} from '../brands';
import {readCampaignRefSchema} from '../campaigns';
import {readImageSchema} from '../images';

// Enums

// Where the landing page comes from: `existing` is the customer's own page,
// personalized in place; `new` is a page generated & hosted by Tinct.
export const landingPageOriginEnum = z.enum(['existing', 'new']);
export type LandingPageOrigin = z.infer<typeof landingPageOriginEnum>;

// `analysis` versions are crawls of an existing-origin page (one per campaign
// analysis run); the other kinds belong to generated/hosted pages.
export const landingPageVersionKindEnum = z.enum(['initial', 'edit', 'regeneration', 'analysis']);
export type LandingPageVersionKind = z.infer<typeof landingPageVersionKindEnum>;

export const landingPageVersionStatusEnum = z.enum(['queued', 'generating', 'ready', 'failed']);
export type LandingPageVersionStatus = z.infer<typeof landingPageVersionStatusEnum>;

// Lifecycle of the packaged preview artifact (the htmlKeys files). Packaging
// runs asynchronously after a version becomes ready, and re-runs on every
// custom-code change; a custom-code save flips affected current versions back
// to `packaging` synchronously while the previous artifact keeps serving.
export const landingPageVersionPackagingStatusEnum = z.enum([
  'pending',
  'packaging',
  'packaged',
  'failed',
]);
export type LandingPageVersionPackagingStatus = z.infer<
  typeof landingPageVersionPackagingStatusEnum
>;

// 1. Reference schema with label field (for dropdowns, selects, links).
// `name` is null for the existing-origin rows auto-created from campaign URLs,
// so `url` is included as the fallback display label.
export const readLandingPageRefSchema = z.object({
  id: z.uuid(),
  name: z.string().nullable(),
  url: z.string().nullable(),
});
export type ReadLandingPageRef = z.infer<typeof readLandingPageRefSchema>;

// 2. Minimal reference schema (for foreign keys)
export const landingPageRefSchema = z.object({
  id: z.uuid(),
});
export type LandingPageRef = z.infer<typeof landingPageRefSchema>;

// Landing-page version (read-only, nested under a landing page)
export const readLandingPageVersionSchema = z.object({
  id: z.uuid().nullable(),
  kind: landingPageVersionKindEnum.nullable(),
  status: landingPageVersionStatusEnum.nullable(),
  // Per-dimension served URLs of the packaged crawled HTML,
  // e.g. {"1200x800": "https://…/1200x800_original.html"}.
  htmlKeys: z.record(z.string(), z.string()).nullable(),
  packagingStatus: landingPageVersionPackagingStatusEnum.nullable(),
  // When the current artifact was written — the cache-busting key and the
  // "re-package landed" signal (re-packaging overwrites the same URLs).
  packagedAt: z.iso.datetime().nullable(),
  // Per-dimension URLs of the raw (pre-rewrite) capture.
  sourceHtmlKeys: z.record(z.string(), z.string()).nullable(),
  previewUrl: z.string().nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type ReadLandingPageVersion = z.infer<typeof readLandingPageVersionSchema>;

// 3. Read schema - all properties nullable (API response)
export const readLandingPageSchema = z.object({
  id: z.uuid().nullable(),
  name: z.string().nullable(),
  slug: z.string().nullable(),
  // The customer's live page URL (existing-origin pages). Null for hosted
  // pages — their live URL is `publishedUrl`.
  url: z.string().nullable(),
  origin: landingPageOriginEnum.nullable(),
  // Statuses are backend-managed and not exhaustively documented client-side,
  // so this stays a plain string (e.g. 'ready').
  status: z.string().nullable(),
  // Metadata extracted from the live page.
  htmlTitle: z.string().nullable(),
  htmlDescription: z.string().nullable(),
  // Preview screenshot / og:image, resolved by the backend from the URL.
  previewImage: z.object(readImageSchema).nullable(),
  // The version whose blocks currently drive personalization.
  currentVersion: readLandingPageVersionSchema.nullable(),
  publishedVersion: readLandingPageVersionSchema.nullable(),
  versions: z.array(readLandingPageVersionSchema).nullable(),
  sourceUrl: z.string().nullable(),
  brief: z.string().nullable(),
  tinctHostname: z.string().nullable(),
  // The tinct-domain URL — always set once the page is published.
  publishedUrl: z.string().nullable(),
  // The workspace custom-domain URL ({baseUrl}{slug}/). Non-null only when the
  // workspace landing-page domain is `active` AND the page is published;
  // prefer it over `publishedUrl` when present.
  organizationPublishedUrl: z.string().nullable(),
  previewUrl: z.string().nullable(),
  organization: z.lazy(() => readOrganizationRefSchema).nullable(),
  brand: z.lazy(() => readBrandRefSchema).nullable(),
  // Reverse-resolved from the owning campaign (the campaign points at the
  // page, 1:1). Read-only.
  campaign: z.lazy(() => readCampaignRefSchema).nullable(),
  publishedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type ReadLandingPage = z.infer<typeof readLandingPageSchema>;

// Legacy exports for backward compatibility
export const landingPageSchema = readLandingPageSchema;
export type LandingPage = ReadLandingPage;
